
import { Shield, Check, X, Info, Settings } from "lucide-react"
import { Card } from "./ui/card"
import { Button } from "./ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { Switch } from "./ui/switch"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { NumberInput } from "./ui/number-input"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { getOrganization, putOrganization } from "../../api/helpers"

export function GovernancePage() {
  const user = useSelector((state: any) => state.auth.user)
  const isAdmin = user?.user_role === "admin" || user?.user_role === "super_admin"
  const [minContributors, setMinContributors] = useState(5)
  const [aggregationPeriod, setAggregationPeriod] = useState("weekly")
  const [languageAnalysis, setLanguageAnalysis] = useState(true)
  const [videoAnalysis, setVideoAnalysis] = useState(true)
  const [lateralVisibility, setLateralVisibility] = useState(true)
  const [individualAnonymity, setIndividualAnonymity] = useState(true)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Load organization data on mount
  useEffect(() => {
    const loadOrganizationData = async () => {
      if (!user?.organization_info?._id) return

      try {
        setLoading(true)
        const response = await getOrganization()
        if (response?.organization) {
          const org = response.organization
          
          // Load emotion_models if they exist
          if (org.emotion_models) {
            setLanguageAnalysis(org.emotion_models.language ?? true)
            setVideoAnalysis(org.emotion_models.face ?? true)
          }
          
          // Load other governance settings if they exist
          if (org.governance_settings) {
            if (org.governance_settings.aggregation) {
              setMinContributors(org.governance_settings.aggregation.min_contributors ?? 5)
              setAggregationPeriod(org.governance_settings.aggregation.aggregation_period ?? "weekly")
            }
            if (org.governance_settings.access_controls) {
              setLateralVisibility(org.governance_settings.access_controls.lateral_visibility ?? true)
              setIndividualAnonymity(org.governance_settings.access_controls.individual_anonymity ?? true)
            }
          }
        }
      } catch (error) {
        console.error("Failed to load organization data:", error)
      } finally {
        setLoading(false)
      }
    }

    loadOrganizationData()
  }, [user?.organization_info?._id])

  const handleSave = async () => {
    if (!user?.organization_info?._id) {
      console.error("Organization ID not found")
      return
    }

    try {
      setSaving(true)
      const data = {
        emotion_models: {
          language: languageAnalysis,
          face: videoAnalysis,
        },
        governance_settings: {
          aggregation: {
            min_contributors: minContributors,
            aggregation_period: aggregationPeriod,
          },
          access_controls: {
            lateral_visibility: lateralVisibility,
            individual_anonymity: individualAnonymity,
          },
        },
      }

      const response = await putOrganization(data, user.organization_info._id)
      if (response?.status === "success") {
        // Optionally show a success message or reload
        console.log("Governance settings saved successfully")
      }
    } catch (error) {
      console.error("Failed to save governance settings:", error)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setMinContributors(5)
    setAggregationPeriod("weekly")
    setLanguageAnalysis(true)
    setVideoAnalysis(true)
    setLateralVisibility(true)
    setIndividualAnonymity(true)
  }

  const getPrivacyControls = () => {
    const controls = []

    controls.push({
      icon: Check,
      color: "text-green-600",
      text: `Data aggregated ${aggregationPeriod}, min. ${minContributors} contributors`,
    })

    if (individualAnonymity) {
      controls.push({
        icon: Check,
        color: "text-green-600",
        text: "Individual identities protected",
      })
    } else {
      controls.push({
        icon: X,
        color: "text-red-600",
        text: "Individual identities may be visible",
      })
    }

    if (lateralVisibility) {
      controls.push({
        icon: Check,
        color: "text-green-600",
        text: "Lateral visibility across peer teams enabled",
      })
    } else {
      controls.push({
        icon: X,
        color: "text-red-600",
        text: "No lateral visibility across peer teams",
      })
    }

    const modalities = []
    if (languageAnalysis) modalities.push("Language")
    if (videoAnalysis) modalities.push("Video")

    if (modalities.length > 0) {
      controls.push({
        icon: Check,
        color: "text-green-600",
        text: `${modalities.join(", ")} analysis enabled`,
      })
    }

    controls.push({
      icon: Info,
      color: "text-blue-600",
      text: "SOC 2 / GDPR compliant",
    })

    return controls
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">Data Governance & Privacy</h1>
          <p className="text-muted-foreground mt-1">
            Configure privacy settings and data governance policies for your organization
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings & Configuration
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground text-lg mb-4">Active Privacy Controls</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {getPrivacyControls().map((control, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <control.icon className={`h-4 w-4 ${control.color}`} />
                    <span>{control.text}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground text-lg">Configuration</h3>
                {!isAdmin && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4" />
                    <span>Only administrators can modify these settings</span>
                  </div>
                )}
              </div>
              <div className="space-y-6">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Access Controls</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="lateral-visibility">Lateral Team Visibility</Label>
                        <Switch
                          id="lateral-visibility"
                          checked={lateralVisibility}
                          onCheckedChange={setLateralVisibility}
                          disabled={!isAdmin}
                          className="data-[state=unchecked]:bg-muted data-[state=unchecked]:border-2 data-[state=unchecked]:border-muted-foreground/30"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="individual-anonymity">Individual Anonymity</Label>
                        <Switch
                          id="individual-anonymity"
                          checked={individualAnonymity}
                          onCheckedChange={setIndividualAnonymity}
                          disabled={!isAdmin}
                          className="data-[state=unchecked]:bg-muted data-[state=unchecked]:border-2 data-[state=unchecked]:border-muted-foreground/30"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Aggregation Settings</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="min-contributors">Minimum Contributors</Label>
                        <NumberInput
                          value={minContributors}
                          onChange={setMinContributors}
                          min={1}
                          max={50}
                          className="w-20"
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="aggregation-period">Aggregation Period</Label>
                        <Select value={aggregationPeriod} onValueChange={setAggregationPeriod} disabled={!isAdmin}>
                          <SelectTrigger className="w-32" disabled={!isAdmin}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-foreground">Data Modalities</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="language-analysis">Language Analysis</Label>
                      <Switch id="language-analysis" checked={languageAnalysis} onCheckedChange={setLanguageAnalysis} disabled={!isAdmin} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="video-analysis">Video Analysis</Label>
                      <Switch id="video-analysis" checked={videoAnalysis} onCheckedChange={setVideoAnalysis} disabled={!isAdmin} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={handleReset} disabled={saving || loading || !isAdmin}>
                    Reset to Defaults
                  </Button>
                  <Button onClick={handleSave} disabled={saving || loading || !isAdmin}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <Card className="p-6">
            <h3 className="font-semibold text-foreground text-lg mb-4">Recent Governance Changes</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-2 border-b">
                <span>Minimum contributors changed from 3 to 5</span>
                <span className="text-muted-foreground">2 days ago</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span>Lateral team visibility disabled</span>
                <span className="text-muted-foreground">1 week ago</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span>Prosody analysis enabled</span>
                <span className="text-muted-foreground">2 weeks ago</span>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
